import { Magnet, Maximize2, Minus, Plus, Ruler } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Toggle } from '@/components/ui/toggle'
import { WithTooltip } from '@/components/ui/tooltip'
import { cn } from '@/components/ui/utils'
import { isStageBody, useEditorStore } from '@/stores/editorStore'
import { pointInPolygon } from '@/geo/polygon'
import type { SnapFlags } from '@/geo/snap'
import type { Pt } from '@/geo/types'

import { kindLabel, useVocab } from './vocab'

/**
 * The strip under the canvas (§3.2): zoom, fit / 1:1, the snapping toggles,
 * the cursor position in percent and what is under it.
 *
 * The cursor readout is deliberately in the same units the document stores
 * (`52.5, 71.0`), so a number seen here can be typed into the properties
 * panel or pasted into a bug report and mean the same thing.
 *
 * It never pushes anything past the edge. What fits is decided by the strip's own width (it is a
 * size container — the picture column is ~660 px wide at a 1024 px window but ~1030 px at 1440):
 * below 768 px the four snapping switches fold into one "Snap" menu, and on a phone the Fit label
 * and the pointer readout step aside. If it still does not fit, it wraps.
 */

type SnapKey = Exclude<keyof SnapFlags, 'grid'>

const SNAPS: { key: SnapKey; label: string; hint: string; aria: string; icon?: typeof Magnet }[] = [
  { key: 'vertices', label: 'Corners', hint: 'Snap to the corners of other shapes', aria: 'Snap to corners', icon: Magnet },
  { key: 'edges', label: 'Edges', hint: 'Snap to the edges of other shapes', aria: 'Snap to edges' },
  { key: 'image_edges', label: 'Picture', hint: 'Snap to strong lines in the picture itself', aria: 'Snap to the picture', icon: Ruler },
  { key: 'horizon', label: 'Horizon', hint: 'Snap to the horizon line', aria: 'Snap to the horizon' },
]

export interface CanvasStatusBarProps {
  cursor: Pt | null
  className?: string
}

export function CanvasStatusBar({ cursor, className }: CanvasStatusBarProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const viewport = useEditorStore((s) => s.viewport)
  const snapping = useEditorStore((s) => s.snapping)
  const toggleSnap = useEditorStore((s) => s.toggleSnap)
  const setViewport = useEditorStore((s) => s.setViewport)
  const fit = useEditorStore((s) => s.fit)

  const under = cursor && isStageBody(doc) ? doc.regions.filter((r) => pointInPolygon(cursor, r.poly)) : []
  const top = under[under.length - 1]
  const onCount = SNAPS.filter((s) => snapping[s.key]).length

  return (
    <div className={cn('@container min-w-0 shrink-0 border-t border-line bg-paper text-xs text-ink-2', className)} data-testid="canvas-status">
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 px-2 py-1.5 @md:gap-x-2 @md:px-3" data-testid="canvas-status-row">
        <WithTooltip label="Zoom out (−)">
          <Button variant="ghost" size="icon" className="size-9 shrink-0 @md:size-11" aria-label="Zoom out" onClick={() => setViewport({ k: Math.max(0.1, viewport.k / 1.2) })}>
            <Minus className="size-4" />
          </Button>
        </WithTooltip>
        <span className="w-10 shrink-0 text-center tabular-nums @md:w-12">{Math.round(viewport.k * 100)}%</span>
        <WithTooltip label="Zoom in (+)">
          <Button variant="ghost" size="icon" className="size-9 shrink-0 @md:size-11" aria-label="Zoom in" onClick={() => setViewport({ k: Math.min(16, viewport.k * 1.2) })}>
            <Plus className="size-4" />
          </Button>
        </WithTooltip>
        <WithTooltip label="Fit the whole picture (Ctrl+0)">
          <Button variant="ghost" size="sm" className="shrink-0 px-2.5" aria-label="Fit" onClick={fit}>
            <Maximize2 className="size-4" />
            <span className="hidden @md:inline">Fit</span>
          </Button>
        </WithTooltip>
        <WithTooltip label="Actual size (Ctrl+1)">
          <Button variant="ghost" size="sm" className="shrink-0 px-2.5" onClick={() => setViewport({ k: 1, tx: 0, ty: 0 })}>
            1:1
          </Button>
        </WithTooltip>

        {/* Room for them: the four snapping switches in a row. Less room: one "Snap" menu holding the same four. */}
        <span className="mx-1 hidden h-4 w-px shrink-0 bg-line @3xl:block" aria-hidden />
        <div className="hidden shrink-0 items-center gap-2 @3xl:flex" role="group" aria-label="Snapping">
          {SNAPS.map((s) => {
            const Icon = s.icon
            return (
              <WithTooltip key={s.key} label={s.hint}>
                <Toggle size="sm" pressed={snapping[s.key]} onPressedChange={() => toggleSnap(s.key)} aria-label={s.aria}>
                  {Icon ? <Icon className="size-4" /> : null} {s.label}
                </Toggle>
              </WithTooltip>
            )
          })}
        </div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="shrink-0 px-2.5 @3xl:hidden" aria-label={`Snapping: ${onCount} of ${SNAPS.length} on`}>
              <Magnet className="size-4" />
              Snap
              <span className="tabular-nums text-ink-3">{onCount}</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" data-testid="snap-menu">
            <DropdownMenuLabel>Snap while drawing to</DropdownMenuLabel>
            {SNAPS.map((s) => (
              <DropdownMenuCheckboxItem
                key={s.key}
                checked={snapping[s.key]}
                onCheckedChange={() => toggleSnap(s.key)}
                // Keep the menu open: switching two of them is the usual thing to do.
                onSelect={(e) => e.preventDefault()}
                title={s.hint}
              >
                {s.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* What is under the pointer: the name gives way first (truncated); left out on a phone, where no mouse points. */}
        <div className="ml-auto hidden min-w-20 flex-1 basis-0 items-center justify-end gap-3 @lg:flex" aria-live="off">
          {top ? <span className="min-w-0 truncate">{top.label ?? kindLabel(vocab, top.kind, 'region_kind')}</span> : null}
          <span className="w-20 shrink-0 text-right tabular-nums">{cursor ? `${cursor[0].toFixed(1)}, ${cursor[1].toFixed(1)}` : '—'}</span>
        </div>
      </div>
    </div>
  )
}
